/** Pure transcript state: segments keyed by id, ordered by index, guarded by seq. */

import type { Segment, ServerEvent, SessionState } from "./protocol.ts";

export type Transcript = {
  sessionId: string | null;
  seq: number;
  state: SessionState | "idle";
  order: string[];
  byId: Record<string, Segment>;
};

export function emptyTranscript(): Transcript {
  return { sessionId: null, seq: 0, state: "idle", order: [], byId: {} };
}

function insertOrdered(order: string[], byId: Record<string, Segment>, seg: Segment): string[] {
  if (order.includes(seg.id)) return order;
  const next = [...order];
  let i = next.length;
  while (i > 0 && (byId[next[i - 1]!]?.index ?? -1) > seg.index) i -= 1;
  next.splice(i, 0, seg.id);
  return next;
}

export function applyEvent(t: Transcript, event: ServerEvent): Transcript {
  switch (event.type) {
    case "ready":
      if (event.sessionId === t.sessionId) return { ...t, state: event.state };
      return { ...emptyTranscript(), sessionId: event.sessionId, state: event.state };

    case "snapshot": {
      const byId: Record<string, Segment> = {};
      const sorted = [...event.segments].sort((a, b) => a.index - b.index);
      for (const seg of sorted) byId[seg.id] = seg;
      return { ...t, seq: event.seq, state: event.state, byId, order: sorted.map((s) => s.id) };
    }

    case "segment": {
      if (event.seq <= t.seq) return t; // replayed or duplicate
      const seg = event.segment;
      const byId = { ...t.byId, [seg.id]: seg };
      return { ...t, seq: event.seq, byId, order: insertOrdered(t.order, byId, seg) };
    }

    case "remove": {
      if (event.seq <= t.seq) return t;
      if (!(event.id in t.byId)) return { ...t, seq: event.seq };
      const byId = { ...t.byId };
      delete byId[event.id];
      return { ...t, seq: event.seq, byId, order: t.order.filter((id) => id !== event.id) };
    }

    case "status": {
      const seq = event.seq ?? t.seq;
      if (seq < t.seq) return t;
      return { ...t, seq, state: event.state };
    }

    case "error": {
      const seq = event.seq ?? t.seq;
      if (seq < t.seq) return t;
      return { ...t, seq, state: event.fatal ? "error" : t.state };
    }

    default:
      return t;
  }
}

export function segments(t: Transcript, track = "main"): Segment[] {
  return t.order
    .map((id) => t.byId[id])
    .filter((s): s is Segment => Boolean(s) && (s!.track ?? "main") === track);
}

/**
 * The segment as a listener who chose `lang` should read it: their own
 * language when they understand the original, else the translation into it.
 */
export function inLanguage(seg: Segment, lang: string | null): Segment {
  if (!lang || lang === seg.targetLang) return seg;
  if (lang === seg.lang) return { ...seg, target: seg.source, targetLang: seg.lang, targetFinal: seg.final };
  const text = seg.translations?.[lang];
  if (text) return { ...seg, target: { stable: text, pending: "" }, targetLang: lang, targetFinal: true };
  // Translations into extra languages start when the sentence is final.
  return { ...seg, target: { stable: "", pending: "…" }, targetLang: lang, targetFinal: false };
}

export function hasText(seg: Segment): boolean {
  return Boolean(seg.source.stable || seg.source.pending || seg.target.stable || seg.target.pending);
}
